import request from "@/utils/Request";
import {ATTEndpoints} from "../endpoints";
import {SubscriptionEndpoints} from "@/services/subscription/endpoints";

export interface OvertimeTrendData {
  weeks: string[];
  T1Overtime: number[];
  T2Overtime: number[];
}

export interface EmployeeTrendData {
  weeks: string[];
  overtimeEmployees: number[];
  thresholdEmployees: number[];
}

export interface EmployeeOvertimeItem {
  personId: string;
  personName: string;
  t1OTHours: number;
  t2OTHours: number;
}

export interface EmployeeOvertimeListData {
  total: number;
  list: EmployeeOvertimeItem[];
}

export interface EmployeeListParams {
  page?: number;
  size?: number;
  keyword?: string;
  minT1?: number;
  maxT1?: number;
  minT2?: number;
  maxT2?: number;
  sortBy?: string;
  order?: string;
}

/**
 * 将后端错误响应转换为可读的错误信息字符串。
 * 后端业务错误（HTTP 200 + code != 0）时 data 可能是一个对象，
 * 直接 new Error(object) 会得到 "[object Object]"，这里统一转成可读文本。
 */
const toErrorMessage = (data: any, fallback: string): string => {
  const raw = data?.data;
  if (typeof raw === 'string' && raw.trim()) {
    return raw;
  }
  if (raw && typeof raw === 'object') {
    const readable =
      (typeof raw.detail === 'string' && raw.detail) ||
      (typeof raw.message === 'string' && raw.message) ||
      (typeof raw.error === 'string' && raw.error) ||
      (typeof raw.msg === 'string' && raw.msg);
    if (readable) {
      return readable;
    }
    try {
      const json = JSON.stringify(raw);
      if (json && json !== '{}') {
        return json;
      }
    } catch {
      /* ignore JSON.stringify errors */
    }
  }
  if (typeof data?.message === 'string' && data.message) {
    return data.message;
  }
  return fallback;
};

/**
 * 判断后端响应是否成功。
 * 兼容多种成功码：数字 0、字符串 "0"、"I0000"，
 * 以及带服务前缀的变体（如 "TAMSI0000"、"MSUBSI0000"）。
 */
const isSuccessCode = (code: any): boolean =>
  code === 0 ||
  code === '0' ||
  code === 'I0000' ||
  (typeof code === 'string' && code.endsWith('I0000'));

/** 将后端可能返回的 subscription_status 宽松转换为布尔值。 */
const toBoolean = (value: any): boolean =>
  value === true ||
  value === 'true' ||
  value === 'True' ||
  value === 1 ||
  value === '1';

/** 将后端可能返回的字符串/数字/null 宽松转换为数字，非法值回退到 fallback。 */
const toNumber = (value: any, fallback = 0): number => {
  if (value === null || value === undefined || value === '') return fallback;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(num) ? fallback : num;
};

/**
 * 查询订阅状态。
 * 并发请求 v1 版（旧）和 v2 版（新）订阅接口，
 * 两个接口的返回值取 or 关系：任一返回已订阅即视为已订阅。
 * 响应体格式（示例）：{ status, code, message, data: { subscription_status, ... } }
 */
const getSubscriptionStatus = async (): Promise<{ subscribed: boolean }> => {
  const statusEndpoints = [
    SubscriptionEndpoints.PAYMENT_STATUS_URI_V1,
    SubscriptionEndpoints.PAYMENT_STATUS_URI,
  ];
  const results = await Promise.allSettled(
    statusEndpoints.map((url) => request.authorizedAxios().get(url))
  );
  if (!results.some((r) => r.status === 'fulfilled')) {
    throw new Error('Failed to fetch subscription status');
  }
  const subscribed = results.some((r) => {
    if (r.status !== 'fulfilled') return false;
    const body = r.value?.data;
    return isSuccessCode(body?.code) && toBoolean(body?.data?.subscription_status);
  });
  return { subscribed };
};

const getOvertimeTrend = async (params?: { startWeek?: string; endWeek?: string; type?: string }): Promise<OvertimeTrendData> => {
  const {data} = await request.authorizedAxios().get(ATTEndpoints.ANALYTICS_OVERTIME_TREND, {params});
  if (isSuccessCode(data?.code)) {
    return data.data;
  }
  throw new Error(toErrorMessage(data, 'Failed to fetch overtime trend'));
};

const getEmployeeTrend = async (): Promise<EmployeeTrendData> => {
  const {data} = await request.authorizedAxios().get(ATTEndpoints.ANALYTICS_EMPLOYEE_TREND);
  if (isSuccessCode(data?.code)) {
    return data.data;
  }
  throw new Error(toErrorMessage(data, 'Failed to fetch employee trend'));
};

const getEmployeeOvertimeList = async (params: EmployeeListParams): Promise<EmployeeOvertimeListData> => {
  const {data} = await request.authorizedAxios().get(ATTEndpoints.ANALYTICS_EMPLOYEES_OVERTIME, {params});
  if (isSuccessCode(data?.code)) {
    const raw = data.data;
    // 后端可能把工时以字符串（如 "8.5"）或 null 返回，统一转成 number，保证类型契约。
    const list = Array.isArray(raw?.list)
      ? raw.list.map((item: any): EmployeeOvertimeItem => ({
          personId: item?.personId ?? '',
          personName: item?.personName ?? '',
          t1OTHours: toNumber(item?.t1OTHours),
          t2OTHours: toNumber(item?.t2OTHours),
        }))
      : [];
    return {
      total: toNumber(raw?.total, list.length),
      list,
    };
  }
  throw new Error(toErrorMessage(data, 'Failed to fetch employee overtime list'));
};

const AnalyticsService = {
  getSubscriptionStatus,
  getOvertimeTrend,
  getEmployeeTrend,
  getEmployeeOvertimeList,
};

export default AnalyticsService;
